import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ErrorBoundary } from "@portal/components";
import { Header } from "../../components";
import { FeedbackToast } from "../shared/FeedbackToast/FeedbackToast";
import { useFeatures } from "../../hooks";
import { IPluginItem, Plugins } from "../../types";
import { loadPlugins, sortPluginsByType, getPluginChildPathPattern } from "../../utils";
import { SystemAdminSingleSpaPluginRenderer } from "../../plugins/core/SystemAdminSingleSpaPluginRenderer";
import { SystemAdminPluginRenderer } from "../../plugins/core/SystemAdminPluginRenderer";
import { initializeImportMap } from "../../singleSpa";
import { useSystemFeatures } from "../../hooks/useSystemFeatures";
import { Account } from "../shared/Account/Account";
import env from "../../env";
import "./SystemAdmin.scss";

const ROUTES = {
  account: "account",
  legal: "legal",
  logout: "/logout",
};

const plugins = loadPlugins();
const releaseVersion = env.REACT_APP_ALP_RELEASE_VERSION;
const gitCommit = env.GIT_COMMIT;
const CURRENT_SYSTEM = env.REACT_APP_CURRENT_SYSTEM;

const SystemAdmin: FC = () => {
  const location = useLocation();
  const [systemFeatures] = useSystemFeatures();
  const [systemAdminPlugins, setSystemAdminPlugins] = useState<Plugins[]>([]);
  const [features] = useFeatures();

  const defaultRoute = useMemo(() => {
    const firstPlugin = systemAdminPlugins.at(0);
    return firstPlugin?.route || "";
  }, [systemAdminPlugins]);

  useEffect(() => {
    if (releaseVersion) {
      const shortRelVersion = releaseVersion.substring(0, releaseVersion.indexOf("-"));
      console.log(`Release version: ${shortRelVersion}`);
    }
    if (gitCommit) {
      console.log(`Git commit: ${gitCommit}`);
    }
  }, []);

  const systemAdminPluginsFlat = useMemo(() => {
    const flatPlugins: IPluginItem[] = [];
    plugins.systemadmin.forEach((plugin) => {
      flatPlugins.push(plugin);
      plugin.children?.forEach((childPlugin) => {
        flatPlugins.push(childPlugin);
      });
    });
    return flatPlugins;
  }, [plugins]);

  const singleSpaApps = useMemo(() => systemAdminPluginsFlat.filter((p) => p.type === "app"), [systemAdminPluginsFlat]);

  const legacyPlugins = useMemo(() => systemAdminPluginsFlat.filter((p) => p.type !== "app"), [systemAdminPluginsFlat]);

  useEffect(() => {
    initializeImportMap(singleSpaApps);
  }, [singleSpaApps]);

  const setupFeatureFlag = useCallback(
    (plugin: IPluginItem) => {
      // Detect and map the feature flag value (true/false) in the data props if key matched
      if (plugin.data && Object.keys(plugin.data).length > 0) {
        Object.keys(plugin.data).forEach((key) => {
          const feature = features?.find((f) => f.feature === key);
          if (feature) {
            plugin.data = {
              ...plugin.data,
              [key]: feature.isEnabled || false,
            };
          }
        });
      }
      return plugin;
    },
    [features]
  );

  useEffect(() => {
    const updateSystemAdminPlugins = () => {
      const displayedSystemAdminPlugins = plugins.systemadmin
        .filter((plugin) => plugin.visible ?? plugin.enabled)
        .reduce<Plugins[]>((acc, item) => {
          const route = item.route;
          const pluginEnv: { [key: string]: boolean } = {};
          if (item.featureFlag) {
            pluginEnv[route] = systemFeatures.includes(item.featureFlag);
          }

          item = setupFeatureFlag(item);

          // Allows plugins not specified in pluginEnv to be shown by default
          // Allows plugins which are enabled based on pluginEnv
          if (!(route in pluginEnv) || pluginEnv[route]) {
            if ("children" in item) {
              acc.push({
                ...item,
                children: item.children
                  ?.map((child) => {
                    child = setupFeatureFlag(child);
                    return child;
                  })
                  .filter((x) => x.visible ?? x.enabled),
              });
            } else {
              acc.push(item);
            }
          }
          return acc;
        }, []);
      setSystemAdminPlugins(displayedSystemAdminPlugins);
    };
    updateSystemAdminPlugins();
  }, [systemFeatures, setupFeatureFlag]);

  const sortedPlugins = useMemo(() => sortPluginsByType(systemAdminPlugins), [systemAdminPlugins]);

  return (
    <div className="systemadmin__container">
      <Header portalType="systemadmin" systemAdminPlugins={sortedPlugins} />
      <main>
        <FeedbackToast />
        {singleSpaApps.map((item: IPluginItem) => {
          const isActiveRoute = location.pathname.includes(`/systemadmin/${item.route}`);
          return (
            <div
              key={item.route}
              style={{
                display: isActiveRoute ? "block" : "none",
                width: "100%",
                height: "100%",
              }}
            >
              <ErrorBoundary name={item.name}>
                <SystemAdminSingleSpaPluginRenderer
                  path={item.pluginPath}
                  route={item.route}
                  type={item.type}
                  system={CURRENT_SYSTEM}
                  data={item?.data}
                  basePath="systemadmin"
                  autoMount={item.autoMount}
                />
              </ErrorBoundary>
            </div>
          );
        })}
        <Routes>
          <Route path="/">
            <Route index element={<Navigate to={defaultRoute} />} />
            <Route path={ROUTES.account} element={<Account portalType="system_admin" />} />
            {legacyPlugins.map((item: IPluginItem) => {
              // Log viewer (a vue app) uses path routing on jobs/* route. The app is mounted in the portal Jobs component.
              // Including "jobs/*" routes avoids a blank screen which is the default when a route cannot be found.
              const items = item.route === "jobs" ? [{ ...item, route: item.route + "/*" }, item] : [item];
              return items.map((item) => (
                <Route
                  key={item.name}
                  path={getPluginChildPathPattern(item)}
                  element={
                    <ErrorBoundary name={item.name} key={item.route}>
                      <SystemAdminPluginRenderer path={item.pluginPath} system={CURRENT_SYSTEM} data={item?.data} />
                    </ErrorBoundary>
                  }
                />
              ));
            })}
          </Route>
        </Routes>
      </main>
    </div>
  );
};

export default SystemAdmin;
